import { zodResolver } from '@hookform/resolvers/zod';
import { Add } from '@mui/icons-material';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { CreateTemplate } from '@thro-types/notification';

import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';

import { useApplicationsQuery, useTemplatesMutation } from 'api/hooks/notifications';
import DetailPage from 'app/components/DetailPage';
import Subheading from 'app/components/DetailPage/components/Subheading';
import InfoBox from 'app/components/InfoBox';
import { VariableCreation } from 'app/components/MailTemplate/VariablesCreation';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { CreateTemplateVariable, TemplateVariables } from 'types/AppTypes';

import { Schema, initialVariablesValues, schema } from './validation';

export const CreateMailTemplate = () => {
  const { t } = useTranslation();
  const methods = useForm<Schema>({
    resolver: zodResolver(schema(t)),
    defaultValues: {
      applicationsId: '',
      description: '',
      name: '',
      html: '',
      subject: '',
      text: '',
      variables: {}
    }
  });
  const dispatch = useDispatch();

  const { data: applications, isLoading } = useApplicationsQuery();

  const { creation } = useTemplatesMutation();

  const [variables, setVariables] = useState([initialVariablesValues()]);

  const descriptionValue = methods.getValues('description');

  const handleUpdateVariables = (updatedVariables: CreateTemplateVariable[]) => {
    const keySet = new Set(updatedVariables.map(v => v.key));

    //Only if all keys are unique it will be updated
    if (keySet.size === updatedVariables.length && !Object.keys(keySet).find(key => key === '')) {
      setVariables(updatedVariables);

      const newVariables: TemplateVariables = arrayToTemplateVariables(updatedVariables);

      methods.setValue('variables', newVariables);
    }
  };

  const arrayToTemplateVariables = (array: CreateTemplateVariable[]): TemplateVariables => {
    return array.reduce((templateVar, createVar) => {
      templateVar[createVar.key] = {
        description: createVar.description,
        scope: {
          html: createVar.scope.html,
          text: createVar.scope.text,
          subject: createVar.scope.subject
        }
      };
      return templateVar;
    }, {} as TemplateVariables);
  };
  const handleSave = async (values: CreateTemplate) => {
    try {
      await creation.mutateAsync(values);
      dispatch(
        AlertActions.success(
          t(translations.pages.articleMessagesSettings.alerts.templateSaveSuccessful())
        )
      );
    } catch (error) {
      dispatch(
        AlertActions.error(
          t(translations.pages.articleMessagesSettings.alerts.templateSaveFailed())
        )
      );
    } finally {
      methods.reset();
      setVariables([initialVariablesValues()]);
    }
  };

  const handleAddVariables = () => {
    const updatedVariables = [...variables];
    updatedVariables.push(initialVariablesValues());
    setVariables(updatedVariables);
  };

  return (
    <DetailPage className="article-messages-settings__content">
      <Subheading
        content={t(translations.pages.articleMessagesSettings.treeViewLabels.createTemplate())}
      />
      <Grid container flexDirection={'column'} gap={2}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSave)}>
            <Grid container spacing={2} justifyContent={'space-between'}>
              <Grid item xs={12} md={6}>
                <FormControl
                  style={{ paddingBottom: '1em' }}
                  {...methods.register('applicationsId')}
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    {t(translations.pages.articleMessagesSettings.createTemplate.applicationName())}
                  </InputLabel>
                  <Select
                    {...methods.register('applicationsId')}
                    error={
                      methods.formState.errors.applicationsId &&
                      Boolean(methods.formState.errors.applicationsId)
                    }
                    label={t(
                      translations.pages.articleMessagesSettings.createTemplate.applicationName()
                    )}
                  >
                    {!isLoading &&
                      applications.map(app => {
                        return <MenuItem value={app.id}>{app.name}</MenuItem>;
                      })}
                  </Select>
                  {methods.formState.errors.applicationsId && (
                    <Typography
                      style={{ marginLeft: '14px' }}
                      fontSize="small"
                      color="error"
                      variant="body2"
                    >
                      {methods.formState.errors.applicationsId?.message}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  {...methods.register('name')}
                  error={methods.formState.errors.name && Boolean(methods.formState.errors.name)}
                  className="article-messages-settings__content__subject"
                  label={t(translations.common.name())}
                  variant="outlined"
                  fullWidth
                  size="small"
                  helperText={methods.formState.errors.name?.message}
                />
                <TextField
                  {...methods.register('description')}
                  error={methods.formState.errors.name && Boolean(methods.formState.errors.name)}
                  className="article-messages-settings__content__subject"
                  label={t(translations.common.description())}
                  variant="outlined"
                  fullWidth
                  size="small"
                  helperText={methods.formState.errors.description?.message}
                />
                <TextField
                  {...methods.register('subject')}
                  error={
                    methods.formState.errors.subject && Boolean(methods.formState.errors.subject)
                  }
                  className="article-messages-settings__content__subject"
                  label={t(translations.common.subject())}
                  variant="outlined"
                  fullWidth
                  size="small"
                  helperText={methods.formState.errors.subject?.message}
                />
                <Controller
                  {...methods.register('html')}
                  control={methods.control}
                  render={({ field }) => (
                    <ReactQuill
                      style={{
                        border: methods.formState.errors.html ? '1px solid red' : '',
                        borderRadius: methods.formState.errors.html ? '4px' : ''
                      }}
                      className="article-messages-settings__content__rte"
                      theme="snow"
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'link'],
                          [
                            { list: 'ordered' },
                            { list: 'bullet' },
                            { indent: '-1' },
                            { indent: '+1' }
                          ],
                          ['clean']
                        ]
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      formats={['bold', 'italic', 'link', 'list', 'bullet', 'indent']}
                    />
                  )}
                />
                {methods.formState.errors.html && (
                  <Typography
                    style={{ marginLeft: '14px' }}
                    fontSize="small"
                    color="error"
                    variant="body2"
                  >
                    {methods.formState.errors.html?.message}
                  </Typography>
                )}
                <Grid container justifyContent="flex-end">
                  <Button
                    className="article-messages-settings__content__save"
                    variant="contained"
                    type="submit"
                  >
                    {t(translations.common.save())}
                  </Button>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6} className="article-messages-settings__content__info">
                {
                  <InfoBox
                    title={t(
                      translations.pages.articleMessagesSettings.templateDescription.heading()
                    )}
                    type="info"
                  >
                    <Grid
                      container
                      className="article-messages-settings__content__info__description"
                    >
                      {descriptionValue}
                      {t(translations.pages.articleMessagesSettings.templateDescription.content())}
                    </Grid>
                    <Grid
                      gap={2}
                      container
                      className="article-messages-settings__content__info__placeholders"
                    >
                      <Button
                        size="small"
                        variant="contained"
                        style={{ color: 'white' }}
                        startIcon={<Add />}
                        onClick={handleAddVariables}
                      >
                        {t(translations.common.add())}
                      </Button>
                      <VariableCreation
                        variables={variables}
                        setVariables={setVariables}
                        ignoreTextScope={false}
                        handleUpdateVariables={handleUpdateVariables}
                      />
                    </Grid>
                  </InfoBox>
                }
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Grid>
    </DetailPage>
  );
};
